import React, { useState, useEffect } from "react";
import ReturnsList from "./ReturnsList";
import { useFont } from "../../contexts/FontContext/FontContext";
import UtilityButton from "../UtilityButton/UtilityButton";
import CreateLink from "./CreateLink";
import Exchanges from "./Exchanges/Exchanges";
import styles from "./ReturnsView.module.css";
import { highlightOptionBorder } from "../utils/common";

const ReturnsView = ({ returnOptionSelected, setReturnOptionSelected, apiUrl, token }) => {
	const [businessName, setBusinessName] = useState(null);
	const [revealReturnsTitle, setRevealReturnsTitle] = useState(false);
	const [returnsButtonsEnabled, setReturnsButtonsEnabled] = useState(false);
	const [fadeInExternalAppButtons, setFadeInExternalAppButtons] = useState(false);
	const [fadeClassExchanges, setFadeClassExchanges] = useState("no_opacity");
	const [fadeClassCreateLink, setFadeClassCreateLink] = useState("no_opacity");
	const [fadeClassSeeAllReturns, setFadeClassSeeAllReturns] = useState("no_opacity");
	const [hoveredReturnOption, setHoveredReturnOption] = useState(null);
	const [showLoader, setShowLoader] = useState(true);
	const [returnsOptionBlurbOpacity, setReturnsOptionBlurbOpacity] = useState({exchanges: "no_opacity", create_link: "no_opacity", see_all_returns: "no_opacity"});
	const fontSchemes = useFont();    
    const fontFamilyOne = fontSchemes.scheme_one; 

	const fadeInReturnsOptions = () => {  
		setShowLoader(true);
		//must reset opacity here. Otherwise, animation won't run properly when returns button in navbar is selected.
		setFadeClassExchanges("no_opacity");
		setFadeClassCreateLink("no_opacity");
		setFadeClassSeeAllReturns("no_opacity");
		setReturnsButtonsEnabled(false);
		resetReturnsOptionBlurbOpacity();
		setTimeout(() => { 
			setFadeClassExchanges("fade_in_bounce"); 
			setShowLoader(false);
		}, 700);
		setTimeout(() => { setFadeClassCreateLink("fade_in_bounce"); }, 1000);
		setTimeout(() => { 
			setFadeClassSeeAllReturns("fade_in_bounce"); 
			setReturnsButtonsEnabled(true);
		}, 1300);
		setTimeout(() => { setFadeInExternalAppButtons(true); }, 1700);
	};

	const highlightReturnOption = (option) => { 
		setHoveredReturnOption(option);
		setReturnsOptionBlurbOpacity({
			exchanges: option === "exchanges" ? "fade_in" : "no_opacity",
			create_link: option === "create_link" ? "fade_in" : "no_opacity",
			see_all_returns: option === "see_all_returns" ? "fade_in" : "no_opacity"
		});
	};

	// Reset all blurbs to "no_opacity" when the mouse leaves
	const resetReturnsOptionBlurbOpacity = () => {
		setHoveredReturnOption(null);
		setReturnsOptionBlurbOpacity({exchanges: "no_opacity", create_link: "no_opacity", see_all_returns: "no_opacity"});
	};

	const selectReturnOption = (option) => { setReturnOptionSelected(option); };

	// get business name for portal url
	useEffect(() => {
		const fetchBusinessName = async () => {
	    	try {
	      		const response = await fetch(`${apiUrl}/api/get_business_name_portal/`, {
	        		method: "GET",
	        		headers: {
	          			"Content-Type": "application/json",
	          			"Authorization": `Bearer ${token}`,
	        		},
	      	});
	    	const result = await response.json();
	    	setBusinessName(result.business_name);
	    	} catch (error) { console.error("Error requesting business name:", error); }
	 	};
	  fetchBusinessName();
	}, [apiUrl, token]);


	useEffect(() => {
		const timer = setTimeout(() => setRevealReturnsTitle(true), 700);
		return () => clearTimeout(timer);
	}, []);

	// Run fadeInReturnsOptions each time returnOptionSelected is set to null
	useEffect(() => {
		if (returnOptionSelected === null) { fadeInReturnsOptions(); }
	}, [returnOptionSelected]);
	
	return (
		<div className={`${styles.returns_view_container} ${fontFamilyOne}`}>
			{!returnOptionSelected && (
				<>
					{fadeInExternalAppButtons && (
						<>
							<div className={styles.external_app_buttons_container}>
								<div className={`${styles.label_generator_button_container} ${styles.link_buttons} fade_in`} onClick={() => window.open("/label_generator", "_blank")}>
									<div className={styles.link_button_images_box}>
										<img className={styles.label_generator_icon} src={`${process.env.PUBLIC_URL}/images/label_generator_icon.png`} />
										<img className={styles.plus_symbol} src={`${process.env.PUBLIC_URL}/images/plus_symbol.png`} />
									</div>
									<div className={styles.link_button_text_box}>
										<p className={styles.label_generator_button_text}>Create a label.</p>
									</div>
								</div>
								<div className={`${styles.receiver_button_container} ${styles.link_buttons} fade_in`} onClick={() => window.open("/home_3pl", "_blank")}>
									<div className={styles.link_button_images_box}>
										<img className={styles.receiver_button_image} src={`${process.env.PUBLIC_URL}/images/receiver_button_image.png`} />
									</div>
									<div className={styles.link_button_text_box}>
										<p className={styles.receiver_button_text}>Go to receiver.</p>
									</div>
								</div>
							</div>
						</>
					)}
					{revealReturnsTitle && <p className={`titles ${styles.returns_view_title} fade_in`}>Get started with returns.</p>}
					{fadeInExternalAppButtons && (
						<div className={styles.portal_button_container}>
							<div className={`${styles.portal_button} ${styles.link_buttons} fade_in`} onClick={() => window.open(`/${businessName}/portal`, "_blank")}>
								<div className={styles.link_button_images_box}>
									<img className={styles.portal_button_icon} src={`${process.env.PUBLIC_URL}/images/portal_icon.png`} />
								</div>
								<div className={styles.link_button_text_box}>
									<p className={styles.receiver_button_text}>See portal.</p>
								</div>
							</div>
						</div>
					)}
					<div className={styles.returns_options_wrapper}>
						{showLoader && (<img className={styles.returns_page_loader} src={`${process.env.PUBLIC_URL}/images/shipwheel_loader.gif`} />)}
						<UtilityButton id="exchanges_label_select" imageId={styles.exchanges_select_image} title="Exchanges" option="exchanges" imageSrc={`${process.env.PUBLIC_URL}/images/exchanges_image.png`} onHover={highlightReturnOption} onLeave={resetReturnsOptionBlurbOpacity} onClick={selectReturnOption} fadeClass={fadeClassExchanges} isSelected={null} isDisabled={!returnsButtonsEnabled} borderStyle={highlightOptionBorder(hoveredReturnOption, "exchanges")}/>
						<UtilityButton id="create_link_select" imageId={styles.create_link_select_image} title="Create link" option="create_link" imageSrc={`${process.env.PUBLIC_URL}/images/create_link_image.png`} onHover={highlightReturnOption} onLeave={resetReturnsOptionBlurbOpacity} onClick={selectReturnOption} fadeClass={fadeClassCreateLink} isSelected={null} isDisabled={!returnsButtonsEnabled} borderStyle={highlightOptionBorder(hoveredReturnOption, "create_link")}/>
						<UtilityButton id="see_all_returns_select" imageId={styles.see_all_returns_select_image} title="See all returns" option="see_all_returns" imageSrc={`${process.env.PUBLIC_URL}/images/see_all_returns_image.png`} onHover={highlightReturnOption} onLeave={resetReturnsOptionBlurbOpacity} onClick={selectReturnOption} fadeClass={fadeClassSeeAllReturns} isSelected={null} isDisabled={!returnsButtonsEnabled} borderStyle={highlightOptionBorder(hoveredReturnOption, "see_all_returns")}/>
					</div>
					<div className={`${styles.returns_selection_information_wrapper}`}>
						<div className={`${styles.returns_blurb_wrapper} ${returnsOptionBlurbOpacity.exchanges}`}>
							<p className={`${styles.returns_blurbs}`}>Start an exchange.</p>
						</div>
						<div className={`${styles.returns_blurb_wrapper} ${returnsOptionBlurbOpacity.create_link}`}>
							<p className={`${styles.returns_blurbs}`}>Create a new return link.</p>
						</div>
						<div className={`${styles.returns_blurb_wrapper} ${returnsOptionBlurbOpacity.see_all_returns}`}>
							<p className={`${styles.returns_blurbs}`}>View or void previous returns.</p>
						</div>
					</div>
				</>
			)}
			{returnOptionSelected === "exchanges" && (<Exchanges setReturnOptionSelected={setReturnOptionSelected} />)}
			{returnOptionSelected === "create_link" && (<CreateLink linkType={"return"} setReturnOptionSelected={setReturnOptionSelected} />)}
			{returnOptionSelected === "see_all_returns" && (<ReturnsList seeAllExchanges={false} setReturnOptionSelected={setReturnOptionSelected} />)}
		</div>
	);
};

export default ReturnsView;